import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { UserAvatar } from '@bonusly/common-user-popover';
import {
	Button,
	ChevronRightOutline,
	Flex,
	Heading,
	Img,
	InformationCircleOutline,
	ModalBody,
	ModalCloseButton,
	ModalHeader,
	rem,
	Stack,
	Text,
	Tooltip,
	VStack,
} from '@bonusly/design-system';
import { useIntegrationsCalendarsLink } from '@bonusly/one-on-ones-queries';
import { useUser } from '@bonusly/user-queries';
import { useFeatureFlag } from '@bonusly/who-am-i';

import googleCalendarIcon from '../../assets/google-calendar-icon.svg';
import outlookIcon from '../../assets/outlook-icon.svg';
import { Variant } from '../../constants';
import { AnimatedBox } from '../animated-box';

interface CalendarIntegrationProps {
	eventId?: string | null;
	meetingId?: string;
	guestId: string;
	onManual: VoidFunction;
	onClose: VoidFunction;
}

export function CalendarIntegration({ eventId, meetingId, guestId, onManual, onClose }: CalendarIntegrationProps) {
	const keyPrefix = 'meeting.modal';
	const { t } = useTranslation('one_on_ones', { keyPrefix });

	const location = useLocation();
	const navigate = useNavigate();
	const { data: guest } = useUser(guestId);
	const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
	const outlookCalendarEnabled = useFeatureFlag(Variant.OutlookCalendar);

	// we will only get the integrations calendar link if connect pressed
	const { data: calendarLinkData, isSuccess } = useIntegrationsCalendarsLink({
		calendarProvider: selectedProvider,
		eventId,
		guestId,
		meetingId,
		redirectPath: `${location.pathname}/calendar-events`,
	});

	const onPressConnect = useCallback((calendarProvider: string) => {
		setSelectedProvider(calendarProvider);
	}, []);

	useEffect(() => {
		if (isSuccess && calendarLinkData) {
			// we get redirect url if not authorized yet or path + guest
			const { calendarProvider, eventId, guestId, path, redirectUrl } = calendarLinkData;

			// if redirect url, navigate to it as it is the auth flow
			if (redirectUrl) {
				window.location.href = redirectUrl;
				return;
			}

			const params = new URLSearchParams();
			params.set('guest_id', guestId);
			params.set('calendar_provider', calendarProvider);
			if (eventId) params.set('event_id', eventId);
			setSelectedProvider(null);
			onClose();
			navigate(`${path}?${params.toString()}`);
		}
	}, [calendarLinkData, isSuccess, navigate, onClose]);

	return (
		<AnimatedBox>
			<ModalHeader>{t('header_create')}</ModalHeader>
			<ModalCloseButton size="lg" borderRadius="full" />

			<ModalBody p={0}>
				<Stack p={5} pt={4} gap={3}>
					<Flex bgColor="bg.canvas" borderRadius={8} p={4} minH={rem(80)} alignItems="center" gap={3} w="full">
						<Flex w={rem(48)}>
							<UserAvatar size="lg" userId={guestId} borderColor="text.onColor" borderWidth={1} />
						</Flex>
						{guest && (
							<Flex justifyContent="space-between" w="full" alignItems="center">
								<Flex flexDir="column" gap={0}>
									<Text size="lg" variant="strong">
										{guest.displayName}
									</Text>
									<Text size="sm" color="text.softest">{`@${guest.username}`}</Text>
								</Flex>
							</Flex>
						)}
					</Flex>

					<Stack spacing={1}>
						<Flex alignItems="center" gap={2}>
							<Heading size="xl">{t('calendar.connect')}</Heading>
							<Tooltip
								label={
									<VStack align="flex-start" spacing={2}>
										<Text fontWeight="bold">{t('calendar.calendar_tooltip_title')}</Text>
										<Text fontSize="sm">{t('calendar.calendar_tooltip_line1')}</Text>
										<Text fontSize="sm">{t('calendar.calendar_tooltip_line2')}</Text>
										<Text fontSize="sm">{t('calendar.calendar_tooltip_line3')}</Text>
									</VStack>
								}
								placement="top"
								hasArrow
							>
								<Flex>
									<InformationCircleOutline color="gray.500" size="sm" _hover={{ color: 'gray.900' }} />
								</Flex>
							</Tooltip>
						</Flex>
						<Text color="text.softer" size="sm">
							{t('calendar.connect_description')}
						</Text>
					</Stack>

					<CalendarItem onClick={() => onPressConnect('google-calendar')}>
						<Img src={googleCalendarIcon} alt={t('calendar.connect_calendar', { provider: 'Google Calendar' })} />
						<Flex flex="1" flexDirection="column">
							<Text fontWeight="medium">{t('calendar.connect_calendar', { provider: 'Google' })}</Text>
							{/* <Text fontSize="xs" color="text.softer">
								{t('calendar.calendar_sync_description')}
							</Text> */}
						</Flex>
					</CalendarItem>

					{outlookCalendarEnabled && (
						<CalendarItem onClick={() => onPressConnect('outlook-calendar')}>
							<Img src={outlookIcon} alt={t('calendar.connect_calendar', { provider: 'Outlook' })} />
							<Flex flex="1" flexDirection="column">
								<Text fontWeight="medium">{t('calendar.connect_calendar', { provider: 'Outlook' })}</Text>
								{/* <Text fontSize="xs" color="text.softer">
									{t('calendar.calendar_sync_description')}
								</Text> */}
							</Flex>
						</CalendarItem>
					)}
					<Button fontSize="sm" onClick={onManual} variant="link" alignSelf="flex-start" aria-label="Manually set date and time">
						Manually set date and time
					</Button>
				</Stack>
			</ModalBody>
		</AnimatedBox>
	);
}

interface CalendarListItemProps {
	children?: React.ReactNode;
	onClick?: VoidFunction;
}

function CalendarItem({ children, onClick }: CalendarListItemProps) {
	return (
		<Flex
			alignItems="center"
			bgColor="secondary.bgAlpha"
			cursor="pointer"
			direction="row"
			gap={2}
			h={14}
			justifyContent="space-between"
			onClick={onClick}
			px={3}
			rounded="md"
			transition="background-color 0.2s"
			_hover={{ bgColor: 'secondary.bgAlphaHover' }}
		>
			{children}
			<ChevronRightOutline color="border.default" size="sm" />
		</Flex>
	);
}
